import { Buffer } from 'buffer'
import { Platform } from 'react-native'
import * as SecureStore from 'expo-secure-store'

import { captureException } from './sentry'
import { getScopedStorageKey } from './storage-scope'

const isWeb = Platform.OS === 'web'
const isClient = typeof window !== 'undefined'
const SECURE_STORE_KEY_PREFIX = 'ss.'

export const toSecureStoreKey = (key: string): string => {
  const base64 = Buffer.from(key, 'utf8').toString('base64')
  const base64url = base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '')

  return `${SECURE_STORE_KEY_PREFIX}${base64url}`
}

interface StorageOptions {
  scoped?: boolean
}

async function resolveStorageKey(
  key: string,
  options?: StorageOptions,
): Promise<string | null> {
  if (options?.scoped === false) {
    return key
  }

  return getScopedStorageKey(key)
}

async function persistItem(storageKey: string, value: string): Promise<void> {
  if (isWeb) {
    if (!isClient) return
    localStorage.setItem(storageKey, value)
    return
  }

  await SecureStore.setItemAsync(toSecureStoreKey(storageKey), value)
}

export const setItem = async (
  key: string,
  value: string,
  options?: StorageOptions,
) => {
  const storageKey = await resolveStorageKey(key, options)
  if (storageKey == null) return

  if (isWeb) {
    try {
      await persistItem(storageKey, value)
    } catch (e) {
      console.error('Local storage is not available:', e)
      captureException(e, {
        feature: 'storage',
        tags: { op: 'write', platform: 'web' },
        extra: { key: storageKey },
      })
    }
  } else {
    await persistItem(storageKey, value)
  }
}

export const setItemOrThrow = async (
  key: string,
  value: string,
  options?: StorageOptions,
) => {
  const storageKey = await resolveStorageKey(key, options)
  if (storageKey == null) return

  await persistItem(storageKey, value)
}

export const getItem = async (key: string, options?: StorageOptions) => {
  const storageKey = await resolveStorageKey(key, options)
  if (storageKey == null) return null

  if (isWeb) {
    if (!isClient) return null
    try {
      return localStorage.getItem(storageKey)
    } catch (e) {
      console.error('Local storage is not available:', e)
      captureException(e, {
        feature: 'storage',
        tags: { op: 'read', platform: 'web' },
        extra: { key: storageKey },
      })
      return null
    }
  } else {
    return await SecureStore.getItemAsync(toSecureStoreKey(storageKey))
  }
}

export const deleteItem = async (key: string, options?: StorageOptions) => {
  const storageKey = await resolveStorageKey(key, options)
  if (storageKey == null) return

  if (isWeb) {
    if (!isClient) return
    try {
      localStorage.removeItem(storageKey)
    } catch (e) {
      console.error('Local storage is not available:', e)
      captureException(e, {
        feature: 'storage',
        tags: { op: 'delete', platform: 'web' },
        extra: { key: storageKey },
      })
    }
  } else {
    await SecureStore.deleteItemAsync(toSecureStoreKey(storageKey))
  }
}
